import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, CheckCircle, ChevronLeft, Lock, FileSignature, Paperclip,
  Scale, Landmark, ShoppingBag, ClipboardList, SearchCheck, X,
} from 'lucide-react';
import { directDraftsApi, draftsApi, isPhoneGateError, errorDetailText } from '../services/api';
import { useDraftsStore } from '../store/draftsStore';
import { useAuthStore } from '../store/authStore';
import { CasePicker } from '../components/cases/CasePicker';
import { PhoneVerifyModal } from '../components/ui';
import { DraftIntakeChat } from '../components/drafts/DraftIntakeChat';
import { MemoPicker } from '../components/research/MemoPicker';
import { DraftTypeInfo } from '../types';
import { isTwa } from '../utils/platform';

const CATEGORY_META: Record<string, { label: string; icon: typeof FileSignature }> = {
  notices: { label: 'Notices', icon: FileSignature },
  criminal: { label: 'Criminal', icon: Scale },
  civil: { label: 'Civil & Writs', icon: Landmark },
  consumer: { label: 'Consumer', icon: ShoppingBag },
  forms: { label: 'Forms & Agreements', icon: ClipboardList },
};
const CATEGORY_ORDER = ['notices', 'criminal', 'civil', 'consumer', 'forms'];

/** Plain-language grounding line, shown at the point of choice (spec §4.4). */
const GROUNDING_EXPLAINER =
  'Memo-based drafts cite the authorities your research verified · Standard drafts cite nothing, so nothing can be wrong.';

/** Quota, in words, at the point of choice (spec §4.4). */
const QUOTA_AT_CHOICE =
  '2 drafts included with every research memo · 3 standard drafts free per month';

export function NewDraftPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { types, fetchTypes, addDraft } = useDraftsStore();
  const user = useAuthStore((s) => s.user);

  const routeState = location.state as {
    matterId?: string;
    seedFields?: Record<string, string | undefined>;
    /** Memo pre-attached — "New draft from this memo" on a memo's Drafts tab. */
    memoJobId?: string;
    memoTitle?: string;
    /** Document pre-attached — "Draft a reply" from Documents (§4.5). */
    doc?: { id: string; name: string };
  } | null;

  const [picked, setPicked] = useState<DraftTypeInfo | null>(null);
  const [includeAuthorities, setIncludeAuthorities] = useState(false);
  const [matterId, setMatterId] = useState<string | null>(routeState?.matterId ?? null);
  // "Base it on a research memo" (§4.4): when attached, generation routes
  // through the memo-draft path (cites the memo's verified authorities).
  const [memoJob, setMemoJob] = useState<{ id: string; title: string } | null>(
    routeState?.memoJobId
      ? { id: routeState.memoJobId, title: routeState.memoTitle ?? 'your research memo' }
      : null,
  );
  const [memoPickerOpen, setMemoPickerOpen] = useState(false);
  // Source document attached inside the interview — mirrored here so the
  // side-by-side attach area reflects it.
  const initialDoc = routeState?.doc ?? null;
  const [chatDoc, setChatDoc] = useState<{ id: string; name: string } | null>(initialDoc);
  const attachOpenRef = useRef<(() => void) | null>(null);

  // Known case facts from "Draft for this case" (MatterDetail) — sent as the
  // opening turn of the intake interview so they're never re-asked.
  const seedFields = routeState?.seedFields;
  const seed = useMemo(() => {
    if (!seedFields) return undefined;
    const LABELS: Record<string, string> = {
      client_name: 'client',
      opposite_party: 'opposite party',
      case_number: 'case number',
      court_name: 'court',
    };
    const parts = Object.entries(seedFields)
      .filter(([, v]) => v && String(v).trim())
      .map(([k, v]) => `${LABELS[k] ?? k.replace(/_/g, ' ')}: ${String(v).trim()}`);
    return parts.length ? `Details from the case file — ${parts.join('; ')}.` : undefined;
  }, [seedFields]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  // Free-tier phone gate (standard drafts only): hold the blocked payload,
  // retry after OTP verify. Memo drafts never trip the gate.
  const [pendingDraft, setPendingDraft] = useState<{
    fields: Record<string, string>;
    instructions?: string;
    input_document_id?: string;
  } | null>(null);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const grouped = useMemo(() => {
    const g: Record<string, DraftTypeInfo[]> = {};
    for (const t of types) (g[t.category] ??= []).push(t);
    return g;
  }, [types]);

  const limit = user?.monthly_drafts_limit;
  const used = user?.monthly_drafts_used ?? 0;
  const limitReached = typeof limit === 'number' && used >= limit;

  const handleGenerate = async (data: {
    fields: Record<string, string>;
    instructions?: string;
    input_document_id?: string;
  }) => {
    if (!picked || isGenerating) return;
    setIsGenerating(true);
    setError('');
    try {
      if (memoJob) {
        // Memo path — same call the memo's Drafts tab has always used. The
        // memo-draft endpoint takes free-text instructions, so the interview's
        // structured answers are serialized into it.
        const labelOf = (key: string) =>
          picked.fields.find((f) => f.key === key)?.label ?? key.replace(/_/g, ' ');
        const parts = Object.entries(data.fields)
          .filter(([, v]) => v && v.trim())
          .map(([k, v]) => `${labelOf(k)}: ${v.trim()}`);
        if (data.instructions?.trim()) parts.push(data.instructions.trim());
        const instructions = parts.join('; ').slice(0, 2000) || undefined;
        const { data: draft } = await draftsApi.create(memoJob.id, picked.type, instructions);
        addDraft(draft);
        navigate(`/app/drafts/${draft.id}`, { state: { draft } });
      } else {
        const { data: draft } = await directDraftsApi.create({
          draft_type: picked.type,
          fields: data.fields,
          instructions: data.instructions,
          matter_id: matterId ?? undefined,
          input_document_id: data.input_document_id,
          include_authorities: includeAuthorities || undefined,
        });
        addDraft(draft);
        navigate(`/app/drafts/${draft.id}`, { state: { draft } });
      }
    } catch (err: any) {
      if (!memoJob && isPhoneGateError(err)) {
        setPendingDraft(data);
      } else {
        setError(errorDetailText(err, 'Draft generation failed. Please try again.'));
      }
      setIsGenerating(false);
    }
  };

  // ---- quota wall (standard drafts only — memo drafts have their own allowance) ----
  if (limitReached && !memoJob) {
    return (
      <div className="p-4 sm:p-6 max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl border border-legal-200 p-10 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-amber-50 rounded-full">
              <Lock className="h-6 w-6 text-amber-500" />
            </div>
          </div>
          <h2 className="text-lg font-semibold text-legal-900 mb-2">
            You've used your {limit} free standard drafts this month
          </h2>
          <p className="text-legal-500 text-sm mb-6">
            Drafts made from a research memo aren't affected — every memo includes 2.
            {isTwa()
              ? ' For unlimited drafting, upgrade at urava.app.'
              : ' Every paid plan includes unlimited drafting.'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => setMemoPickerOpen(true)}
              data-track="draft-wall-memo-instead"
              className="px-5 py-2.5 bg-white border border-legal-300 text-legal-800 rounded-lg text-sm font-medium hover:border-accent hover:text-accent-700 transition-colors"
            >
              Draft from a research memo instead
            </button>
            {!isTwa() && (
              <button
                onClick={() => navigate('/pricing')}
                data-track="draft-wall-see-plans"
                className="px-6 py-2.5 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors"
              >
                See plans
              </button>
            )}
          </div>
        </div>
        <MemoPicker
          open={memoPickerOpen}
          onClose={() => setMemoPickerOpen(false)}
          title="Base it on a research memo"
          onSelect={(job) => setMemoJob({ id: job.id, title: job.title })}
        />
      </div>
    );
  }

  // ---- step 2: conversational intake (replaced the form, July 2026) ----
  if (picked) {
    return (
      <div className="p-4 sm:p-6 max-w-2xl mx-auto">
        <button
          onClick={() => {
            setPicked(null);
            setError('');
            setIncludeAuthorities(false);
            setChatDoc(initialDoc);
          }}
          className="flex items-center space-x-1.5 text-sm text-legal-500 hover:text-legal-700 mb-6 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>All document types</span>
        </button>

        <div className="mb-6">
          <h1 className="font-display text-2xl font-medium text-legal-900">{picked.label}</h1>
          <p className="text-legal-500 mt-1 text-sm">
            Just describe the case — type or dictate, any language. Anything not covered
            becomes a [PLACEHOLDER] for you to complete.
          </p>
        </div>

        {error && (
          <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Two optional attachments, side by side (spec §4.4): memo grounding
            and the source document. Grounding is a parameter, not two products. */}
        <div data-tour="draft-attach" className="mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {memoJob ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-accent-50/70 border border-accent/40 rounded-xl">
                <span className="flex items-center gap-2 min-w-0 text-sm text-legal-800">
                  <BookOpen className="h-4 w-4 text-accent-700 flex-shrink-0" />
                  <span className="truncate">
                    <span className="text-legal-500">Backed by memo:</span> {memoJob.title}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setMemoJob(null)}
                  aria-label="Remove attached memo"
                  className="text-legal-400 hover:text-legal-700 flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-track="draft-attach-memo"
                onClick={() => setMemoPickerOpen(true)}
                className="flex items-center justify-center gap-2 px-3 py-2.5 border border-dashed border-legal-300 rounded-xl text-sm font-medium text-legal-600 hover:border-accent hover:text-accent-700 hover:bg-accent-50/50 transition-colors"
              >
                <BookOpen className="h-4 w-4" />
                Base it on a research memo
              </button>
            )}
            {chatDoc ? (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-accent-50/70 border border-accent/30 rounded-xl min-w-0">
                <Paperclip className="h-4 w-4 text-accent-700 flex-shrink-0" />
                <span className="text-sm text-legal-800 truncate">{chatDoc.name}</span>
                <CheckCircle className="h-3.5 w-3.5 text-accent-700 flex-shrink-0 ml-auto" />
              </div>
            ) : (
              <button
                type="button"
                data-track="draft-attach-document"
                onClick={() => attachOpenRef.current?.()}
                className="flex items-center justify-center gap-2 px-3 py-2.5 border border-dashed border-legal-300 rounded-xl text-sm font-medium text-legal-600 hover:border-accent hover:text-accent-700 hover:bg-accent-50/50 transition-colors"
              >
                <Paperclip className="h-4 w-4" />
                Attach a source document
              </button>
            )}
          </div>
          <p className="text-[11px] text-legal-400 mt-1.5">{GROUNDING_EXPLAINER}</p>
          <p className="text-[11px] text-legal-500 mt-0.5">{QUOTA_AT_CHOICE}</p>
        </div>

        {/* Memo drafts inherit their context from the memo; a case is picked
            only for standard drafts. */}
        {!memoJob && (
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-legal-400">File under a case (optional)</span>
            <CasePicker value={matterId} onChange={setMatterId} compact />
          </div>
        )}

        <div className="bg-white rounded-2xl border border-legal-200 p-4 sm:p-6">
          <DraftIntakeChat
            spec={picked}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
            seed={seed}
            initialDoc={initialDoc}
            onDocChange={setChatDoc}
            attachOpenRef={attachOpenRef}
          />
        </div>

        {!memoJob && picked.citation_policy === 'optional_light' && (
          <label className="flex items-start gap-2.5 cursor-pointer select-none mt-4">
            <input
              type="checkbox"
              checked={includeAuthorities}
              onChange={(e) => setIncludeAuthorities(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-legal-300 text-accent focus:ring-accent"
            />
            <span className="text-sm text-legal-600">
              <span className="font-medium text-legal-800 inline-flex items-center gap-1">
                <SearchCheck className="h-3.5 w-3.5" /> Add verified authorities
              </span>{' '}
              <span className="text-legal-400">
                — searches Indian legal databases and cites only link-verified sources (slower)
              </span>
            </span>
          </label>
        )}
        <p className="text-xs text-legal-400 text-center mt-4">
          First draft for advocate review — always verify before filing or serving.
        </p>

        <MemoPicker
          open={memoPickerOpen}
          onClose={() => setMemoPickerOpen(false)}
          title="Base it on a research memo"
          onSelect={(job) => setMemoJob({ id: job.id, title: job.title })}
        />

        <PhoneVerifyModal
          open={pendingDraft !== null}
          onClose={() => setPendingDraft(null)}
          onVerified={() => {
            const data = pendingDraft;
            setPendingDraft(null);
            if (data) handleGenerate(data);
          }}
        />
      </div>
    );
  }

  // ---- step 1: type picker ----
  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center space-x-2 text-legal-500 hover:text-legal-700 mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="text-sm">Back</span>
      </button>

      <div className="mb-6">
        <h1 className="font-display text-3xl font-medium text-legal-900">Draft a document</h1>
        <p className="text-legal-500 mt-1.5 text-[15px]">
          Standard Indian formats with statutory checkpoints built in.
          {memoJob ? ` Backed by memo: ${memoJob.title}.` : ' No research memo needed.'}
        </p>
      </div>

      {types.length === 0 ? (
        <div className="bg-white rounded-2xl border border-legal-200 p-10 text-center text-legal-400 text-sm">
          Loading document types…
        </div>
      ) : (
        <div data-tour="draft-type-picker" className="space-y-6">
          {CATEGORY_ORDER.filter((c) => grouped[c]?.length).map((cat) => {
            const meta = CATEGORY_META[cat];
            return (
              <div key={cat}>
                <h2 className="flex items-center gap-2 text-sm font-semibold text-legal-500 uppercase tracking-wide mb-2">
                  <meta.icon className="h-4 w-4" />
                  {meta.label}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {grouped[cat].map((t) => (
                    <button
                      key={t.type}
                      onClick={() => setPicked(t)}
                      data-track="draft-type-pick"
                      className="text-left p-4 rounded-lg border border-legal-200 bg-white hover:border-accent hover:bg-accent-50 transition-colors card-lift"
                    >
                      <p className="text-sm font-medium text-legal-900">{t.label}</p>
                      <p className="text-xs text-legal-500 mt-1">{t.blurb}</p>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
